import { ActionKeys } from '~/actions/constants';
import { showDialog } from '~/actions/dialog.actions';
import * as ordersBatchActions from '~/actions/orders-batch.actions';
import * as ordersActions from '~/actions/orders.actions';
import { sendTrackEvent } from '~/actions/prod-analytics.actions';
import * as shipmentsActions from '~/actions/shipments.actions';
import * as transactionsActions from '~/actions/transactions.actions';
import axios, { AxiosResponse } from 'axios';
import { push } from 'connected-react-router';
import flatten from 'lodash-es/flatten';
import uniq from 'lodash-es/uniq';
import { OrdersBatchState } from '~/reducers/orders-batch';
import { reset, startSubmit, stopSubmit } from 'redux-form';
import { call, delay, put, race, select, take, TakeEffect, takeLatest } from 'redux-saga/effects';
import { selectState } from '~/sagas/index';
import {
  getBatchEntryData,
  getIsAnyBatchEntryASplitOrder,
  getOrderObjectIdByBatchEntry,
} from '~/selectors/batch-entry.selectors';
import { getIsOldExpChosen } from '~/selectors/features.selectors';
import { isAutoOrderEmailEnabled } from '~/selectors/notifications.selectors';
import {
  getBatchCurrentStep,
  getBatchEntries,
  getBatchEntryByShipmentId,
  getBatchEntryOutboundShipment,
  getBatchExcludedOrderIds,
  getBatchId,
  getBatchInvalidOrders,
  getBatchShipments,
  getBatchStore,
  getBatchTransactionsData,
  getIgnoredOrdersIds,
  getOutboundBatchEntryFromOrderId,
  getUserBatchInputFromBatchEntryId,
  getWasOrdersFetchTriggered,
} from '~/selectors/orders-batch.selectors';
import {
  getIsSidePanelCheckout,
  getOrdersData,
  getOrdersRemainingForLabelDownload,
} from '~/selectors/orders.selectors';
import { getRatesData, getSelectedRates } from '~/selectors/rates.selectors';
import { getPathname } from '~/selectors/router.selectors';
import { getShipmentByShipmentId } from '~/selectors/shipments.selectors';
import { getHasDefaultMergedLabels } from '~/selectors/user.selectors';
import * as ordersService from '~/services/orders.service';
import Order, {
  fromJSON as orderFromJSON,
  JSONOrder,
  OrderSelection,
  toJSON as orderToJSON,
} from '~/types/Order';
import * as ordersBatch from '~/types/OrdersBatch';
import Rate from '~/types/Rate';
import Shipment from '~/types/Shipment';
import Transaction, { TransactionStatus } from '~/types/Transaction';
import { trackingHelper } from '~/utils/TrackingHelper';
import {
  LABEL_PURCHASED_EVENT,
  RATES_UPDATED_EVENT,
  TrackingReference,
} from '~/utils/TrackingHelper/types';
import {
  getGenericErrorsFromResponseData,
  requestUpdatedTrack,
  trackLabelPurchased,
  trackRequestedUpdatedResponse,
} from '~/utils/TrackingHelper/utils';
import { listToMapById, listToMapByObjectId } from '~/utils/list';
import {
  getSuccessfulTransactionsCount,
  getTransactionsData,
} from '~/selectors/transactions.selectors';

// TODO: Make batchWorker use this?
export function* fetchBatch(action: ReturnType<typeof ordersBatchActions.fetchOrdersBatch>) {
  try {
    const batchId = action.batchId;
    const response: AxiosResponse<ordersBatch.JSONOrdersBatch> = yield call(
      ordersService.getBatch,
      batchId,
    );
    const payload: ordersBatchActions.FetchOrdersBatchPayload = {
      batch: ordersBatch.fromJSON(response.data),
      currentBatchEntries: {},
    };
    yield put(ordersBatchActions.fetchOrdersBatchSuccess(payload));
  } catch (err) {
    yield put(ordersBatchActions.fetchOrdersBatchError());
  }
}

export function* fetchBatchOrdersData(
  action: ReturnType<typeof ordersBatchActions.fetchOrdersBatchSuccess>,
) {
  const wasOrdersFetchTriggered: boolean = yield selectState<boolean>(getWasOrdersFetchTriggered);
  const ordersFetched: string[] = [];

  if (wasOrdersFetchTriggered) return;

  const batchOrders: ordersBatch.BatchOrder[] = action.payload.batch.orders;
  const storedOrders: Record<string, Order> =
    yield selectState<Record<string, Order>>(getOrdersData);

  // Get batch entries from the payload, they are not in the store yet
  const batchEntriesByOrderObjectId = action.payload.batch.batchEntries.reduce<
    Record<string, ordersBatch.BatchEntry>
  >((hashMap, batchEntry) => {
    return {
      ...hashMap,
      [batchEntry.orderObjectId]: batchEntry,
    };
  }, {});

  for (const batchOrder of batchOrders) {
    const batchEntry = batchEntriesByOrderObjectId[batchOrder.objectId];

    if (
      !storedOrders[batchOrder.objectId] &&
      batchEntry.objectStatus !== ordersBatch.BatchEntryStatus.EXCLUDED_ORDER
    ) {
      ordersFetched.push(batchOrder.objectId);
      yield put(ordersActions.fetchOrder(batchOrder.objectId));
    }
  }

  yield put(ordersBatchActions.ordersFetchTriggered(ordersFetched));
}

export function* fetchOrderShipmentRates(
  action: ReturnType<typeof ordersBatchActions.fetchOrdersBatchSuccess>,
) {
  const currentBatchStep: ordersBatch.BatchSteps =
    yield selectState<ordersBatch.BatchSteps>(getBatchCurrentStep);
  const newBatch = action.payload.batch;
  const currentShipments = action.payload.currentShipments;
  const allShipmentsNew = listToMapByObjectId(newBatch.shipments);
  let shipmentsToFetch: string[] = [];

  // We won't check for the step right away since it could be
  // in the third step and we would need to fetch the shipments.
  // This code fetches the shipments only once when we are at the
  // LABELS step.
  if (
    currentBatchStep === ordersBatch.BatchSteps.LABELS &&
    Object.keys(currentShipments!).length === 0 &&
    newBatch.shipmentGroupStatus === ordersBatch.BatchGroupStatus.COMPLETE
  ) {
    for (const objectId of Object.keys(allShipmentsNew)) {
      const trackingReference = trackingHelper.getTrackingReference(
        RATES_UPDATED_EVENT.name,
        'other',
        {
          rateIds: undefined,
        },
      );
      yield put(shipmentsActions.fetchShipment(objectId, trackingReference));
    }
  }

  // If the active step is not the rates one. Do nothing.
  if (currentBatchStep !== ordersBatch.BatchSteps.RATES) return;

  // If it's the first time the batch is fetched (no shipments in the store)
  // and shipments are done we get them
  if (
    Object.keys(currentShipments!).length === 0 ||
    newBatch.shipmentGroupStatus === ordersBatch.BatchGroupStatus.COMPLETE
  ) {
    shipmentsToFetch = Object.keys(allShipmentsNew!);
  } else {
    shipmentsToFetch = Object.keys(currentShipments!).reduce(
      (shipmentsList: string[], currentShipmentKey: string) => {
        const shipment: ordersBatch.BatchShipment = currentShipments![currentShipmentKey];
        const newShipment: ordersBatch.BatchShipment = allShipmentsNew[currentShipmentKey];

        if (!newShipment) {
          return shipmentsList;
        }

        return shipment.numRates !== newShipment.numRates
          ? [...shipmentsList, newShipment.objectId]
          : shipmentsList;
      },
      [],
    );
  }

  for (const objectId of shipmentsToFetch) {
    const trackingReference = trackingHelper.getTrackingReference(
      RATES_UPDATED_EVENT.name,
      'other',
      {
        rateIds: undefined,
      },
    );
    yield put(shipmentsActions.fetchShipment(objectId, trackingReference));
  }

  if (newBatch.shipmentGroupStatus === ordersBatch.BatchGroupStatus.COMPLETE) {
    yield put(ordersBatchActions.pollBatchOrdersEnd());
  }
}

export function* batchWorker(batchId: number, trackingReference?: TrackingReference) {
  while (true) {
    const batchShipments: OrdersBatchState['shipments'] =
      yield selectState<OrdersBatchState['shipments']>(getBatchShipments);
    const batchEntries: OrdersBatchState['batchEntries'] =
      yield selectState<OrdersBatchState['batchEntries']>(getBatchEntries);
    const batchTransactionsData: OrdersBatchState['transactions']['data'] =
      yield selectState<OrdersBatchState['transactions']['data']>(getBatchTransactionsData);

    try {
      const response: AxiosResponse<ordersBatch.JSONOrdersBatch> = yield call(
        ordersService.getBatch,
        batchId,
      );
      const payload: ordersBatchActions.FetchOrdersBatchPayload = {
        batch: ordersBatch.fromJSON(response.data),
        currentShipments: batchShipments.data,
        currentBatchEntries: batchEntries.data,
        currentTransactions: batchTransactionsData,
        trackingReference,
      };
      yield put(ordersBatchActions.fetchOrdersBatchSuccess(payload));
      yield delay(500);
    } catch (err) {
      yield put(ordersBatchActions.fetchOrdersBatchError());
    }
  }
}

export const getTransactionsToFetch = ({
  currentTransactions,
  allTransactionsNew,
}: {
  currentTransactions: Record<string, ordersBatch.BatchTransaction> | undefined;
  allTransactionsNew: Record<string, ordersBatch.BatchTransaction>;
}) => {
  return Object.keys(allTransactionsNew).reduce<ordersBatch.BatchTransaction[]>(
    (list, objectId) => {
      const newTransaction: ordersBatch.BatchTransaction = allTransactionsNew[objectId];
      const currentTransaction: ordersBatch.BatchTransaction = currentTransactions![objectId];

      // TODO: Check that this ones are not already in the store
      if (
        !currentTransaction ||
        currentTransaction.objectStatus !== newTransaction.objectStatus ||
        // We need the transaction with error state as well
        newTransaction.objectStatus === TransactionStatus.ERROR
      ) {
        list.push(newTransaction);
      }

      return list;
    },
    [],
  );
};

export const getBulkTransactionStatus = <T extends { objectStatus: TransactionStatus }>(
  transactions: T[],
) => {
  if (!transactions.length) return 'none';

  return transactions.every((t) => t.objectStatus === transactions[0].objectStatus)
    ? transactions[0].objectStatus
    : 'mixed';
};

export const getBulkTransactionStatusAnyError = <T extends { objectStatus: TransactionStatus }>(
  transactions: T[],
) => {
  if (!transactions.length) return false;

  return transactions.some((t) => TransactionStatus.ERROR === t.objectStatus);
};

export const getTransactionsRateIds = (
  transactionObjects: { [transactionId: string]: Readonly<Transaction> },
  transactionIds: string[],
): string[] =>
  transactionIds.reduce((rateIds: string[], transactionId: string) => {
    return transactionObjects[transactionId]
      ? [transactionObjects[transactionId].rate, ...rateIds]
      : rateIds;
  }, []);

export const getSelectedRateIds = (
  trackingReference: TrackingReference,
  selectedRates,
  batchEntryData,
): string[] => {
  if (!trackingReference.trackedOrderDetailsByShipment) return [];
  const batchEntryIds = Object.keys(trackingReference.trackedOrderDetailsByShipment);
  return batchEntryIds
    ? batchEntryIds.map((id) => selectedRates[batchEntryData[id].shipmentObjectId])
    : [];
};

export function* fetchBatchOrderLabels(
  action: ReturnType<typeof ordersBatchActions.fetchOrdersBatchSuccess>,
) {
  const { currentTransactions, trackingReference, batch } = action.payload;
  const currentBatchStep: ordersBatch.BatchSteps =
    yield selectState<ordersBatch.BatchSteps>(getBatchCurrentStep);
  // If the active step is not the labels one. Do nothing.
  if (
    currentBatchStep !== ordersBatch.BatchSteps.ORDERS_PAGE &&
    currentBatchStep !== ordersBatch.BatchSteps.LABELS
  ) {
    return;
  }

  const allTransactionsNew = listToMapByObjectId(batch.transactions);
  const transactionsToFetch = getTransactionsToFetch({
    currentTransactions,
    allTransactionsNew,
  });

  for (const transaction of transactionsToFetch as ordersBatch.BatchTransaction[]) {
    yield put(transactionsActions.fetchTransaction(transaction.objectId));

    if (
      currentBatchStep === ordersBatch.BatchSteps.ORDERS_PAGE &&
      transaction.objectStatus !== TransactionStatus.QUEUED
    ) {
      const orderObjectId: Maybe<string> = yield select(getOrderObjectIdByBatchEntry, {
        batchEntryId: transaction.batchEntryId,
      });
      if (orderObjectId) {
        yield put(ordersActions.fetchOrder(orderObjectId));
        yield put(ordersActions.fetchOrderBatchEntry(orderObjectId));
      }
    }
  }

  const hasSingleTransactionError =
    transactionsToFetch.length === 1 &&
    transactionsToFetch[0].objectStatus === TransactionStatus.ERROR;

  const outboundGroupIsCompleted =
    batch.transactionGroupStatus === ordersBatch.BatchGroupStatus.COMPLETE;
  const returnGroupIsCompleted =
    batch.returnTransactionGroupStatus === ordersBatch.BatchGroupStatus.NOT_APPLICABLE ||
    batch.returnTransactionGroupStatus === ordersBatch.BatchGroupStatus.COMPLETE;
  const isOldExpChosen: boolean = yield selectState<boolean>(getIsOldExpChosen);
  const isSidePanelCheckout: boolean = yield selectState<boolean>(getIsSidePanelCheckout);
  const successFulTransactionCount: number = yield selectState<number>(
    getSuccessfulTransactionsCount,
  );
  const isFirstLabelPurchase = successFulTransactionCount === 0;
  const batchEntryData = yield selectState(getBatchEntryData);
  const transactionObjects = yield selectState<{ [transactionId: string]: Readonly<Transaction> }>(
    getTransactionsData,
  );

  if (outboundGroupIsCompleted && returnGroupIsCompleted) {
    if (!isOldExpChosen && !hasSingleTransactionError) {
      const hasAutomatedEmailNotifications: boolean =
        yield selectState<boolean>(isAutoOrderEmailEnabled);

      const hasMergedDocuments: boolean = yield selectState<boolean>(getHasDefaultMergedLabels);
      if (trackingReference && transactionsToFetch.length > 0) {
        const status = getBulkTransactionStatus(transactionsToFetch);
        const transactionIds = transactionsToFetch.map((t) => t.objectId);
        const rateIds = transactionIds
          ? getTransactionsRateIds(transactionObjects, transactionIds)
          : [];

        if (transactionIds.length > 0) {
          trackLabelPurchased(
            trackingReference,
            LABEL_PURCHASED_EVENT.operations.TRANSACTION_COMPLETED,
            {
              transactionIds,
              rateIds,
              status,
              isFirstLabelPurchase,
            },
          );
        }
        if (getBulkTransactionStatusAnyError(transactionsToFetch)) {
          if (isSidePanelCheckout) {
            yield put(
              ordersActions.confirmCheckoutWithErrors(
                hasAutomatedEmailNotifications,
                hasMergedDocuments,
              ),
            );
          }
        } else {
          yield put(
            ordersActions.confirmCheckout(hasAutomatedEmailNotifications, hasMergedDocuments),
          );
        }
      } else {
        yield put(
          ordersActions.confirmCheckout(hasAutomatedEmailNotifications, hasMergedDocuments),
        );
      }
    }

    if (!isOldExpChosen && hasSingleTransactionError) {
      if (isSidePanelCheckout) {
        yield put(ordersActions.confirmCheckoutWithErrors());
      }
      if (trackingReference) {
        const selectedRates = yield selectState(getSelectedRates);
        const rateIds = getSelectedRateIds(trackingReference, selectedRates, batchEntryData);

        trackLabelPurchased(trackingReference, LABEL_PURCHASED_EVENT.operations.SAVE_ATTEMPTED, {
          rateIds,
        });
      }
    }
    yield put(ordersBatchActions.pollBatchOrdersEnd());
  }
}

export function* batchWatcher() {
  while (true) {
    const action: TakeEffect = yield take(ActionKeys.ORDERS_BATCH_POLL_START);

    const result: { timeout: boolean } = yield race({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      task: call(
        batchWorker,
        (action as unknown as ReturnType<typeof ordersBatchActions.pollBatchOrdersStart>).payload
          .batchId,
        (action as unknown as ReturnType<typeof ordersBatchActions.pollBatchOrdersStart>).payload
          .trackingReference,
      ),
      cancel: take([ActionKeys.ORDERS_BATCH_POLL_END, ActionKeys.ORDERS_BATCH_CLEAN_UP]),
      timeout: delay(CONFIG.batchPollTimeout, true),
    });

    if (result.timeout) {
      yield put(ordersBatchActions.pollBatchOrdersTimeout());
    }
  }
}

export function* savePackageInformation(
  action: ReturnType<typeof ordersBatchActions.savePackageInformation>,
) {
  try {
    const packageInformation = ordersBatch.userBatchInputToJSON(
      action.payload.shipmentGenerationRequest,
    );
    yield call(
      ordersService.saveBatchPackageInformation,
      action.payload.batchId,
      packageInformation,
    );
    const path: string = yield selectState<string>(getPathname);
    const pathWithoutTrailingSlashes = path
      .split('')
      .filter((char, index) => !(char === '/' && index === path.length - 1))
      .join('');
    yield put(ordersBatchActions.savePackageInformationSuccess());
    yield put(push({ pathname: `${pathWithoutTrailingSlashes}/rates` }));
  } catch (err) {
    yield put(ordersBatchActions.savePackageInformationError());
  }
}

export function* regenerateBatchEntry(
  action: ReturnType<typeof ordersBatchActions.regenerateBatchEntry>,
) {
  try {
    const currentBatchStep: ordersBatch.BatchSteps =
      yield selectState<ordersBatch.BatchSteps>(getBatchCurrentStep);
    const packageInformation = ordersBatch.userBatchInputToJSON(
      action.payload.shipmentGenerationRequest,
    );
    // Check that we are at the step that we want
    if (currentBatchStep !== ordersBatch.BatchSteps.RATES) return;

    yield call(
      ordersService.regenerateBatchEntry,
      action.payload.batchId,
      action.payload.batchEntryId,
      packageInformation,
    );

    yield put(ordersBatchActions.pollBatchOrdersStart(action.payload.batchId));
    yield put(ordersBatchActions.regenerateBatchEntrySuccess(action.payload.batchEntryId));
    yield put(ordersActions.fetchOrder(action.payload.orderObjectId));

    if (action.payload.shipmentId) {
      yield put(ordersBatchActions.removeSelectedRate(action.payload.shipmentId));
    }
  } catch (err) {
    yield put(ordersBatchActions.regenerateBatchEntryError());
  }
}

export function* createTransactions(
  action: ReturnType<typeof ordersBatchActions.createBatchTransactions>,
) {
  const { trackingReference } = action.payload;
  try {
    const excludedOrders: readonly string[] =
      yield selectState<readonly string[]>(getBatchExcludedOrderIds);
    const ignoredOrders: readonly string[] =
      yield selectState<readonly string[]>(getIgnoredOrdersIds);
    const isOldExpChosen: boolean = yield selectState<boolean>(getIsOldExpChosen);
    const currentBatchStep: ordersBatch.BatchSteps =
      yield selectState<ordersBatch.BatchSteps>(getBatchCurrentStep);

    const isOldX =
      isOldExpChosen ||
      (!isOldExpChosen && currentBatchStep !== ordersBatch.BatchSteps.ORDERS_PAGE);

    // Excluding orders is a feature that is only available in the oldX batch flow
    // For some reason this array is populated in NewX which leads to endless rates spinner
    // due to the rates generation never succeeding since batch entry is in excluded order state
    // This should prevent order batch entries from getting excluded incorrectly
    const excludedAndIgnoredOrders = isOldX ? [...excludedOrders, ...ignoredOrders] : [];

    // const result: AxiosResponse<{ batch_entries: { id: number }[] }> = yield call(
    const result: Awaited<ReturnType<typeof ordersService.createBatchTransactions>> = yield call(
      ordersService.createBatchTransactions,
      action.payload.batchId,
      action.payload.rates,
      excludedAndIgnoredOrders,
    );
    trackRequestedUpdatedResponse(trackingReference, result);

    yield put(
      ordersBatchActions.fetchTransactionData(
        result?.data.remaining_labels_until_limit,
        result?.data.label_limit_renewal_date,
      ),
    );

    const fullpath: string = yield selectState<string>(getPathname);
    const splitpath = fullpath.split('/');
    const path = splitpath.splice(0, splitpath.length - 1).join('/');

    yield put(ordersBatchActions.createBatchTransactionsSuccess(action.payload.rates.length));

    if (isOldX) {
      yield put(push({ pathname: `${path}/labels` }));
    } else {
      const batchId: number | undefined | null = yield selectState<number | undefined | null>(
        getBatchId,
      );
      // The compiler thinks this might be missing
      // Try to do it without assertion.
      yield put(ordersBatchActions.pollBatchOrdersStart(batchId as number, trackingReference));
    }
  } catch (err) {
    if (err.response && err.response.data && err.response.data.remaining_labels_until_limit) {
      yield put(
        ordersBatchActions.fetchTransactionData(
          err.response.data.remaining_labels_until_limit,
          err.response.data.label_limit_renewal_date,
        ),
      );
    }

    if (axios.isAxiosError(err) && err.response) {
      requestUpdatedTrack(trackingReference, {
        url: err.config.url || '',
        isError: err.response.status > 300,
        status: err.response.status,
        message: getGenericErrorsFromResponseData(err) || undefined,
      });
    }

    if (err.response && err.response.data && err.response.data.detail) {
      const errorMessage: string = err.response.data.detail;

      const isOldExpChosen: boolean = yield selectState<boolean>(getIsOldExpChosen);
      if (isOldExpChosen) {
        yield put(
          showDialog(
            {
              messages: [errorMessage],
              title: 'Something went wrong',
              closeButtonText: 'Close',
            },
            'error',
          ),
        );
      } else {
        yield put(ordersBatchActions.createBatchTransactionsError(errorMessage));
      }
    } else {
      if (
        err.response &&
        err.response.data &&
        err.response.data.remaining_labels_until_limit &&
        !err.response.data.detail
      ) {
        yield put(
          ordersBatchActions.createBatchTransactionsError(
            'You have exceeded the monthly label limit for your plan.',
          ),
        );
      } else {
        yield put(
          ordersBatchActions.createBatchTransactionsError(
            'We hit an unexpected error. Please try again.',
          ),
        );
      }
    }
  }
}

export function* generatePackingSlips() {
  try {
    const currentBatchStep: ordersBatch.BatchSteps =
      yield selectState<ordersBatch.BatchSteps>(getBatchCurrentStep);
    // If the active step is not the labels one. Do nothing.
    if (
      currentBatchStep !== ordersBatch.BatchSteps.ORDERS_PAGE &&
      currentBatchStep !== ordersBatch.BatchSteps.LABELS
    ) {
      return;
    }

    yield put(ordersBatchActions.generatePackingSlipsFromBatch());
    const batchStore: OrdersBatchState = yield selectState<OrdersBatchState>(getBatchStore);

    // We need to download slips for all the selected orders along with
    // just purchased order, if there are selected orders
    const remianingOrders: OrderSelection = yield selectState<OrderSelection>(
      getOrdersRemainingForLabelDownload,
    );
    if (Object.keys(remianingOrders).length) {
      const batchOrders = batchStore.batchEntries.byOrderObjectId;
      const allOrders = { ...remianingOrders, ...batchOrders };
      const orderObjectIDs = Object.keys(allOrders);
      const batchEntryIDs = orderObjectIDs.reduce<number[]>(
        (accum, orderObjectID) => [...accum, ...allOrders[orderObjectID]],
        [],
      );
      const response: AxiosResponse<{ slip_url: string }> = yield call(
        ordersService.getOrderPackingSlip,
        orderObjectIDs,
        batchEntryIDs,
      );
      yield put(
        ordersBatchActions.generatePackingSlipsFromBatchSuccess({
          slipUrl: response.data.slip_url,
        }),
      );
    } else {
      const response: AxiosResponse<{ slip_url: string }> = yield call(
        ordersService.downloadPackingSlips,
        // The compiler thinks this might be missing.
        batchStore?.data?.id as number,
      );
      yield put(
        ordersBatchActions.generatePackingSlipsFromBatchSuccess({
          slipUrl: response.data.slip_url,
        }),
      );
    }
  } catch (err) {
    yield put(
      ordersBatchActions.generatePackingSlipsFromBatchError(
        'We hit an unexpected error. Please try again.',
      ),
    );
  }
}

export function* generateAllLabels() {
  try {
    const currentBatchStep: ordersBatch.BatchSteps =
      yield selectState<ordersBatch.BatchSteps>(getBatchCurrentStep);
    // If the active step is not the labels one. Do nothing.
    if (
      currentBatchStep !== ordersBatch.BatchSteps.ORDERS_PAGE &&
      currentBatchStep !== ordersBatch.BatchSteps.LABELS
    ) {
      return;
    }

    yield put(ordersBatchActions.generateAllLabels());
    const batchStore: OrdersBatchState = yield selectState<OrdersBatchState>(getBatchStore);

    // We need to download labels for all the selected orders along with
    // just purchased order, if there are selected orders
    const remainingOrders: OrderSelection = yield selectState<OrderSelection>(
      getOrdersRemainingForLabelDownload,
    );
    if (Object.keys(remainingOrders).length) {
      const batchOrders = batchStore.batchEntries.byOrderObjectId;
      const allOrders = { ...remainingOrders, ...batchOrders };
      const orderObjectIDs = Object.keys(allOrders);
      const batchEntryIDs = orderObjectIDs.reduce<number[]>(
        (accum, orderObjectID) => [...accum, ...allOrders[orderObjectID]],
        [],
      );
      const response: AxiosResponse<{ url: string }> = yield call(
        ordersService.getAllLabels,
        orderObjectIDs,
        batchEntryIDs,
      );
      yield put(ordersBatchActions.generateAllLabelsSuccess({ labelsUrl: response.data.url }));
    } else {
      const response: AxiosResponse<{ labels_url: string }> = yield call(
        ordersService.downloadAllLabels,
        // The compiler thinks this might be missing.
        batchStore.data?.id as number,
      );
      yield put(
        ordersBatchActions.generateAllLabelsSuccess({ labelsUrl: response.data.labels_url }),
      );
    }
  } catch (err) {
    if (err.response && err.response.data) {
      yield put(ordersBatchActions.generateAllLabelsError(err.response.data));
    } else {
      yield put(
        ordersBatchActions.generateAllLabelsError('We hit an unexpected error. Please try again.'),
      );
    }
  }
}

export function* fetchOrdersErrors(
  action: ReturnType<typeof ordersBatchActions.fetchOrdersBatchSuccess>,
) {
  const currentBatchEntriesList = Object.values(action.payload.currentBatchEntries || {});
  const newBatchEntries = listToMapById(action.payload.batch.batchEntries);
  let batchEntriesToFetch: ordersBatch.BatchEntry[] = [];

  if (currentBatchEntriesList.length === 0) {
    batchEntriesToFetch = Object.values(newBatchEntries).filter(ordersBatch.isFailedBatchEntry);
  } else {
    batchEntriesToFetch = currentBatchEntriesList.reduce(
      (list: ordersBatch.BatchEntry[], batchEntry) => {
        const newBatchEntry = newBatchEntries[batchEntry.id];

        if (
          batchEntry.objectStatus !== newBatchEntry.objectStatus &&
          ordersBatch.isFailedBatchEntry(newBatchEntry)
        ) {
          return [...list, newBatchEntry];
        }

        return list;
      },
      [],
    );
  }

  for (const batchEntry of batchEntriesToFetch) {
    yield put(ordersActions.fetchOrder(batchEntry.orderObjectId));
  }
}

export function* fetchInvalidBatchEntries() {
  const invalidOrders: Order[] = yield selectState<Order[]>(getBatchInvalidOrders);

  for (const order of invalidOrders) {
    yield put(ordersActions.fetchOrder(order.objectId));
  }
}

export function* changeAllRates(action: ReturnType<typeof ordersBatchActions.changeAllRates>) {
  const rates: { [ratesId: string]: Rate } = yield select(getRatesData);

  const ratesWithSelectedServiceLevel: Rate[] = Object.values(rates).filter(
    (rate: Rate) => rate.servicelevel.token === action.payload.serviceLevelToken,
  );
  const excludedOrdersIds: readonly string[] =
    yield selectState<readonly string[]>(getBatchExcludedOrderIds);
  const excludedOrdersIdsMap = excludedOrdersIds.reduce<Record<string, boolean>>(
    (hashMap, orderId) => {
      return { ...hashMap, [orderId]: true };
    },
    {},
  );

  for (const rate of ratesWithSelectedServiceLevel) {
    const batchEntry: ordersBatch.BatchEntry = yield select(getBatchEntryByShipmentId, {
      shipmentObjectId: rate.shipment,
    });

    if (!excludedOrdersIdsMap[batchEntry.orderObjectId]) {
      const orderSelection: OrderSelection = {
        [batchEntry.orderObjectId]: [batchEntry.id],
      };
      const trackingReference = trackingHelper.getTrackingReference(
        RATES_UPDATED_EVENT.name,
        'order_table',
        {
          rateIds: [rate.objectId],
        },
      );
      yield put(
        shipmentsActions.selectRate(
          rate.shipment,
          rate.objectId,
          true,
          trackingReference,
          orderSelection,
          action.payload.serviceLevelToken,
        ),
      );
    }
  }
}

export function* updateBatchOrder(action: ReturnType<typeof ordersBatchActions.updateBatchOrder>) {
  try {
    const order = action.payload.order;
    yield put(startSubmit(action.formName));

    if (action.isUpdatingAddress || !action.useImportedWeight) {
      const jsonOrder = orderToJSON(order);
      const response: AxiosResponse<JSONOrder> = yield call(ordersService.updateOrder, jsonOrder);
      const updatedOrder = orderFromJSON(response.data);

      yield put(ordersActions.updateOrderSuccess(updatedOrder));
    }

    if (action.isUpdatingAddress) {
      yield put(ordersBatchActions.slideBatchInlineEditModal());
    } else {
      yield put(ordersBatchActions.closeBatchLineEditModal());

      let shipment: Maybe<Shipment> = null;
      const selectorProps = {
        orderId: order.objectId,
      };

      const outboundBatchEntry: ordersBatch.BatchEntry = yield select(
        getOutboundBatchEntryFromOrderId,
        selectorProps,
      );
      const previousUserBatchInput: ordersBatch.UserBatchInput = yield select(
        getUserBatchInputFromBatchEntryId,
        {
          batchEntryId: outboundBatchEntry.id,
        },
      );
      const outboundShipment: Maybe<ordersBatch.BatchShipment> = yield select(
        getBatchEntryOutboundShipment,
        selectorProps,
      );

      if (outboundShipment) {
        shipment = yield select(getShipmentByShipmentId, {
          shipmentId: outboundShipment.objectId,
        });
      }

      let updatedUserBatchInput = previousUserBatchInput;

      if (!action.isUpdatingAddress) {
        updatedUserBatchInput = {
          ...previousUserBatchInput,
          parcel: {
            ...action.payload.parcel,
            useImportedOrderWeight: action.useImportedWeight || false,
            // This is missing a length key/value
            // Do it without `as`?
          } as ordersBatch.Parcel,
        };
      }

      const batchId: number | undefined | null = yield selectState<number | undefined | null>(
        getBatchId,
      );

      yield put(
        ordersBatchActions.regenerateBatchEntry(
          // The compiler things this might be missing.
          // Do it without `as`?
          batchId as number,
          outboundBatchEntry.id,
          updatedUserBatchInput,
          shipment ? shipment.objectId : '',
          order.objectId,
        ),
      );
    }

    yield put(stopSubmit(action.formName));
    yield put(reset(action.formName));
  } catch (e) {
    // TODO: server error handling
  }
}

export function* fetchUserBatchInput(
  action: ReturnType<typeof ordersBatchActions.fetchUserBatchInput>,
) {
  const batchEntryId = action.batchEntryId;

  try {
    const batchId: number | undefined | null = yield selectState<number | undefined | null>(
      getBatchId,
    );
    const response: AxiosResponse<ordersBatch.JSONUserBatchInput> = yield call(
      ordersService.getUserBatchInput,
      // The compiler thinks batchId might be missing
      // Do it without `as`?
      batchId as number,
      batchEntryId,
    );
    yield put(
      ordersBatchActions.fetchUserBatchInputSuccess(
        batchEntryId,
        ordersBatch.userBatchInputFromJSON(response.data),
      ),
    );
  } catch (err) {
    yield put(ordersBatchActions.fetchUserBatchInputError(batchEntryId, err.toString()));
  }
}

export function* createAndBuyBatch(
  action: ReturnType<typeof ordersBatchActions.createAndBuyBatch>,
) {
  const { trackingReference } = action.payload;
  try {
    const batchEntryIds = flatten(Object.values(action.payload.orderSelection));
    // Send create batch request. Order Ids can be empty since those are only in this service for oldx
    const response: AxiosResponse<ordersBatch.JSONOrdersBatch> = yield call(
      ordersService.createBatch,
      [],
      batchEntryIds,
    );
    const parsedBatch = ordersBatch.fromJSON(response.data);
    yield put(ordersActions.createBatchSuccess(parsedBatch));

    // Buy rates
    yield put(
      ordersBatchActions.createBatchTransactions(
        parsedBatch.id,
        action.payload.rateObjectIds,
        trackingReference,
      ),
    );
    const rates: HashMap<string, Rate> = yield selectState<HashMap<string, Rate>>(getRatesData);
    const transactedRates: Rate[] = action.payload.rateObjectIds.map((x) => rates[x]);

    const transactionAmount: number = transactedRates.reduce(
      (total, { amountLocal }: Rate) => total + Number(amountLocal),
      0,
    );
    const serviceLevels: string = transactedRates
      .reduce<
        string[]
      >((serviceLevels, { servicelevel }) => uniq([...serviceLevels, servicelevel.name]), [])
      .join(', ');

    const isSplitOrder: boolean = yield select(getIsAnyBatchEntryASplitOrder, { batchEntryIds });

    yield put(
      sendTrackEvent('Purchase', {
        'Transaction Amount': transactionAmount,
        'Service Level': serviceLevels,
        'Is Split Order': isSplitOrder,
      }),
    );
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      requestUpdatedTrack(trackingReference, {
        url: err.response.config.url || '',
        isError: err.response.status > 300,
        status: err.response.status,
        message: getGenericErrorsFromResponseData(err.response.data) || undefined,
      });
    }

    yield put(
      ordersActions.createBatchError('We were not able to process your request. Please try again.'),
    );
  }
}

export default [
  batchWatcher(),
  takeLatest(ActionKeys.ORDERS_BATCH_FETCH_SUCCESS, fetchBatchOrdersData),
  takeLatest(ActionKeys.ORDERS_BATCH_FETCH_SUCCESS, fetchBatchOrderLabels),
  takeLatest(ActionKeys.ORDERS_BATCH_FETCH_SUCCESS, fetchOrderShipmentRates),
  takeLatest(ActionKeys.ORDERS_BATCH_FETCH_SUCCESS, fetchOrdersErrors),
  takeLatest(ActionKeys.ORDERS_BATCH_FETCH, fetchBatch),
  takeLatest(ActionKeys.ORDERS_BATCH_SAVE_PACKAGE_INFORMATION, savePackageInformation),
  takeLatest(ActionKeys.ORDERS_BATCH_ENTRY_REGENERATE, regenerateBatchEntry),
  takeLatest(ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS, createTransactions),
  takeLatest(ActionKeys.ORDERS_BATCH_POLL_END, generatePackingSlips),
  takeLatest(ActionKeys.ORDERS_BATCH_POLL_END, generateAllLabels),
  takeLatest(ActionKeys.ORDERS_BATCH_CHANGE_ALL_RATES, changeAllRates),
  takeLatest(ActionKeys.ORDERS_BATCH_FETCH_INVALID_ORDERS, fetchInvalidBatchEntries),
  takeLatest(ActionKeys.ORDERS_BATCH_UPDATE_ORDER, updateBatchOrder),
  takeLatest(ActionKeys.ORDERS_BATCH_FETCH_USER_INPUT, fetchUserBatchInput),
  takeLatest(ActionKeys.ORDERS_BATCH_CREATE_AND_BUY, createAndBuyBatch),
];
